<!-- src\views\admin\AdminDashboard.vue -->
<template>
	<div class="admn_element-screen" data-model-id="admn_9979:335">
		<div class="admn_div-40" :class="{ 'menu-collapsed': !isMenuExpanded }">
			<AdminHeader />

			<AdminSidebarExpanded v-if="isMenuExpanded" />
			<AdminSidebarCollapsed v-else />

			<!-- Основной контент -->
			<main
				class="admn_main-content"
				:class="{ 'admn_main-content--courses': activeMenu === 4 }"
			>
				<transition name="admn_content_fade" mode="out-in">
					<!-- 1. Дашборд -->
					<div
						v-if="activeMenu === 1 && canViewDashboardSection"
						key="dashboard"
						class="admn_dashboard_layout"
					>
						<StatsCards />
						<div class="admn_dashboard_charts">
							<ActivityChart />
							<RecentActions />
						</div>
					</div>

					<!-- 2. Студенты -->
					<StudentsView v-else-if="activeMenu === 2 && canViewUsersSection" key="students" />

					<!-- 3. Преподаватели -->
					<TeachersView v-else-if="activeMenu === 3 && canViewUsersSection" key="teachers" />

					<!-- 4. Курсы -->
					<CoursesView v-else-if="activeMenu === 4 && canViewCoursesSection" key="courses" />

					<!-- 5. Аналитика -->
					<AnalyticsView v-else-if="activeMenu === 5 && canViewAnalyticsSection" key="analytics" />

					<!-- 6. Отчёты -->
					<ReportsView v-else-if="activeMenu === 6 && canViewReportsSection" key="reports" />

					<!-- 7. Настройки -->
					<SettingsView v-else-if="activeMenu === 7 && canViewSettingsSection" key="settings" />

					<!-- Отчёты и другие разделы -->
					<div v-else key="other" class="admn_other_content">
						<h1 class="admn_content_title">
							{{ getMenuTitle(activeMenu) }}
						</h1>
						<p class="admn_content_description">
							Этот раздел находится в разработке.
						</p>
						<div class="admn_content_placeholder">
							<svg
								width="100"
								height="100"
								viewBox="0 0 100 100"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<circle
									cx="50"
									cy="50"
									r="40"
									stroke="#2563EA"
									stroke-width="4"
									stroke-dasharray="10 5"
								/>
								<text
									x="50"
									y="55"
									text-anchor="middle"
									fill="#2563EA"
									font-size="40"
									font-weight="bold"
								>
									{{ activeMenu }}
								</text>
							</svg>
							<p class="admn_placeholder_text">Раздел в разработке</p>
						</div>
					</div>
				</transition>
			</main>
		</div>
	</div>
</template>

<script setup>
import { ref, provide, watch, onMounted, nextTick, defineAsyncComponent, computed } from 'vue'
import { storeToRefs } from 'pinia'
import AdminHeader from '@/components/admin/layout/AdminHeader.vue'
import AdminSidebarExpanded from '@/components/admin/layout/AdminSidebarExpanded.vue'
import AdminSidebarCollapsed from '@/components/admin/layout/AdminSidebarCollapsed.vue'
import StatsCards from '@/components/admin/dashboard/StatsCards.vue'
import ActivityChart from '@/components/admin/dashboard/ActivityChart.vue'
import RecentActions from '@/components/admin/dashboard/RecentActions.vue'
import { useAdminNavigation } from '@/composables/useAdminNavigation'
import { useAppStore } from '@/stores/useAppStore'

// Асинхронная загрузка компонентов админки
const StudentsView = defineAsyncComponent(() =>
	import('@/components/admin/students/StudentsView.vue')
)
const TeachersView = defineAsyncComponent(() =>
	import('@/components/admin/teachers/TeachersView.vue')
)
const CoursesView = defineAsyncComponent(() =>
	import('@/components/admin/courses/CoursesView.vue')
)
const AnalyticsView = defineAsyncComponent(() =>
	import('@/components/admin/analytics/AnalyticsView.vue')
)
const ReportsView = defineAsyncComponent(() =>
	import('@/components/admin/reports/ReportsView.vue')
)
const SettingsView = defineAsyncComponent(() =>
	import('@/components/admin/settings/SettingsView.vue')
)

// Store
const appStore = useAppStore()
const { user } = storeToRefs(appStore)
const isSuperAdmin = computed(() =>
	String(user.value?.email || '').trim().toLowerCase() === 'super@metall.edu'
)
const canViewUsersSection = computed(() =>
	isSuperAdmin.value || appStore.hasPermission('view_users')
)
const canViewDashboardSection = computed(() =>
	isSuperAdmin.value || appStore.hasPermission('view_analytics')
)
const canViewCoursesSection = computed(() =>
	isSuperAdmin.value ||
	appStore.hasPermission('view_courses') ||
	appStore.hasPermission('create_courses') ||
	appStore.hasPermission('edit_courses') ||
	appStore.hasPermission('delete_courses') ||
	appStore.hasPermission('publish_courses') ||
	appStore.hasPermission('manage_content')
)
const canViewAnalyticsSection = computed(() =>
	isSuperAdmin.value || appStore.hasPermission('view_analytics')
)
const canViewReportsSection = computed(() =>
	isSuperAdmin.value ||
	appStore.hasPermission('view_analytics') ||
	appStore.hasPermission('export_analytics')
)
const canViewSettingsSection = computed(() =>
	isSuperAdmin.value ||
	appStore.hasPermission('view_settings') ||
	appStore.hasPermission('manage_settings') ||
	appStore.hasPermission('view_roles') ||
	appStore.hasPermission('manage_roles') ||
	appStore.hasPermission('manage_users')
)

// CSS переменные для ширины сайдбара
const isMenuExpanded = ref(true)
const SIDEBAR_STATE_KEY = 'admin_sidebar_expanded'
let isInitialized = false

// Обновляем CSS переменную
const sidebarWidth = computed(() => isMenuExpanded.value ? '325px' : '93px')

const saveSidebarState = (expanded) => {
	try {
		const userId = user.value?.id || user.value?.email || 'default'
		const key = `${SIDEBAR_STATE_KEY}_${userId}`
		localStorage.setItem(key, JSON.stringify({ expanded, timestamp: Date.now() }))
	} catch (error) {
		console.error('[AdminDashboard] Failed to save sidebar state:', error)
	}
}

const restoreSidebarState = () => {
	try {
		const userId = user.value?.id || user.value?.email || 'default'
		const key = `${SIDEBAR_STATE_KEY}_${userId}`
		const savedState = localStorage.getItem(key)
		if (savedState) {
			const state = JSON.parse(savedState)
			isMenuExpanded.value = state.expanded
		}
	} catch (error) {
		console.error('[AdminDashboard] Failed to restore sidebar state:', error)
	}
}

const toggleMenu = () => {
	isMenuExpanded.value = !isMenuExpanded.value
}

watch(isMenuExpanded, (newValue) => {
	if (isInitialized) {
		saveSidebarState(newValue)
	}
// Обновляем ширину
	document.documentElement.style.setProperty('--admin-sidebar-width', sidebarWidth.value)
})

// Навигация админки с сохранением состояния
const {
	activeMenu,
	setActiveMenu,
	getMenuTitle,
} = useAdminNavigation({
	defaultMenuId: 1,
	persistState: true,
})

watch(
	[
		activeMenu,
		canViewDashboardSection,
		canViewUsersSection,
		canViewCoursesSection,
		canViewAnalyticsSection,
		canViewReportsSection,
		canViewSettingsSection,
	],
	([menuId, canViewDashboard, canViewUsers, canViewCourses, canViewAnalytics, canViewReports, canViewSettings]) => {
		const firstAllowedMenuId =
			(canViewDashboard && 1) ||
			(canViewUsers && 2) ||
			(canViewCourses && 4) ||
			(canViewAnalytics && 5) ||
			(canViewReports && 6) ||
			(canViewSettings && 7) ||
			1

		const isMenuAllowed =
			(menuId === 1 && canViewDashboard) ||
			((menuId === 2 || menuId === 3) && canViewUsers) ||
			(menuId === 4 && canViewCourses) ||
			(menuId === 5 && canViewAnalytics) ||
			(menuId === 6 && canViewReports) ||
			(menuId === 7 && canViewSettings)

		if (!isMenuAllowed) {
			setActiveMenu(firstAllowedMenuId)
		}
	},
	{ immediate: true }
)

onMounted(() => {
	restoreSidebarState()
// Provide для дочерних компонентов
	document.documentElement.style.setProperty('--admin-sidebar-width', sidebarWidth.value)
	
	nextTick(() => {
		isInitialized = true
	})
})

/* CSS переменные */
provide('isMenuExpanded', isMenuExpanded)
provide('toggleMenu', toggleMenu)
provide('activeMenu', activeMenu)
provide('setActiveMenu', setActiveMenu)
provide('sidebarWidth', sidebarWidth)
provide('canViewDashboardSection', canViewDashboardSection)
provide('canViewUsersSection', canViewUsersSection)
provide('canViewCoursesSection', canViewCoursesSection)
provide('canViewAnalyticsSection', canViewAnalyticsSection)
provide('canViewReportsSection', canViewReportsSection)
provide('canViewSettingsSection', canViewSettingsSection)
</script>

<style scoped>
/* Главный контейнер */
:root {
	--admin-sidebar-width: 325px;
	--admin-sidebar-collapsed-width: 93px;
	--admin-header-height: 84px;
	--admin-content-padding: 30px;
}

.admn_element-screen {
	background-color: #f5f7fa;
	display: flex;
	justify-content: center;
	min-height: 100vh;
	width: 100%;
	overflow-x: hidden;
}

.admn_div-40 {
	min-height: 100vh;
	position: relative;
	width: 100%;
	max-width: 1920px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	--admin-sidebar-expanded-width: 325px;
	--admin-sidebar-expanded-left: 20px;
	--admin-sidebar-collapsed-left: 20px;
	--admin-sidebar-gap: 20px;
	--admin-sidebar-offset: calc(
		var(--admin-sidebar-expanded-left) +
		var(--admin-sidebar-expanded-width) +
		var(--admin-sidebar-gap)
	);
	--admin-sidebar-offset-collapsed: calc(
		var(--admin-sidebar-collapsed-left) +
		var(--admin-sidebar-collapsed-width, 93px) +
		var(--admin-sidebar-gap)
	);
}

/* Основной контент */
.admn_main-content {
	position: relative;
	margin: 20px 20px 20px var(--admin-sidebar-offset);
	min-height: calc(100vh - var(--admin-header-height, 84px) - 40px);
	transition: margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
	overflow-y: auto;
}

.admn_main-content--courses {
	height: calc(100vh - var(--admin-header-height, 84px) - 30px);
	min-height: calc(100vh - var(--admin-header-height, 84px) - 30px);
	overflow: hidden;
}

.menu-collapsed .admn_main-content {
	margin-left: var(--admin-sidebar-offset-collapsed);
}

@media (min-width: 1601px) and (max-width: 1800px) {
	.admn_div-40 {
		--admin-sidebar-expanded-width: 280px;
		--admin-sidebar-expanded-left: 16px;
	}
}

@media (min-width: 1367px) and (max-width: 1600px) {
	.admn_div-40 {
		--admin-sidebar-expanded-width: 260px;
		--admin-sidebar-expanded-left: 14px;
	}
}

@media (max-width: 1366px) {
	.admn_div-40 {
		--admin-sidebar-expanded-width: 240px;
		--admin-sidebar-expanded-left: 12px;
	}
}

/* Dashboard Layout */
.admn_dashboard_layout {
	display: flex;
	flex-direction: column;
	gap: 16px;
	width: 100%;
	height: calc(100vh - var(--admin-header-height) - 60px);
	overflow: hidden;
}

.admn_dashboard_charts {
	display: grid;
	grid-template-columns: 1.6fr 1fr;
	gap: 16px;
	width: 100%;
	flex: 1;
	min-height: 0;
}

.admn_dashboard_charts > * {
	min-width: 0;
}

/* Transitions */
.admn_content_fade-enter-active,
.admn_content_fade-leave-active {
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.admn_content_fade-enter-from {
	opacity: 0;
	transform: translateY(20px);
}

.admn_content_fade-leave-to {
	opacity: 0;
	transform: translateY(-20px);
}

/* Заглушка */
.admn_other_content {
	background: #ffffff;
	border-radius: 16px;
	padding: 40px;
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
	min-height: 600px;
}

.admn_content_title {
	font-family: 'Poppins', sans-serif;
	font-size: 28px;
	font-weight: 700;
	color: #1a1a1a;
	margin: 0 0 12px 0;
}

.admn_content_description {
	font-family: 'Poppins', sans-serif;
	font-size: 15px;
	color: #666666;
	margin: 0 0 40px 0;
}

.admn_content_placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 300px;
	gap: 20px;
}

.admn_placeholder_text {
	font-family: 'Poppins', sans-serif;
	font-size: 16px;
	font-weight: 500;
	color: #2563ea;
	margin: 0;
}

@media (min-width: 1601px) and (max-width: 1800px) {
	.admn_dashboard_charts {
		grid-template-columns: 1.6fr 1fr;
	}
}

@media (min-width: 1367px) and (max-width: 1600px) {
	.admn_dashboard_charts {
		grid-template-columns: 1.5fr 1fr;
	}
}

@media (min-width: 769px) and (max-width: 1366px) {
	.admn_dashboard_charts {
		grid-template-columns: 1.4fr 1fr;
	}
}

@media (max-width: 768px) {
	.admn_main-content {
		margin: 16px;
	}

	.admn_dashboard_charts {
		grid-template-columns: 1fr;
	}
}
</style>

<!-- Глобальные переменные для админки и других компонентов -->
<style>
:root {
	--admin-sidebar-width: 325px;
	--admin-sidebar-collapsed-width: 93px;
	--admin-header-height: 84px;
}
</style>
